import { isNamedCssColor } from "shared/utils/isNamedCssColor"

export const colors = {
  accent: "#3389FF",
  dark: "#333333",
  darkMed: "#4D4D4D",
  blueGrey: "#77969F",
  lightBlue: "#0095E2",
  lightLilac: "#E8E0FF",
  med: "#999999",
  neutralDark: "#DCDCDC",
  neutral: "#F2F3F4",
  white: "#FFFFFF",
  error: "#FF4545",
  black70: "#000000B3",
  successBackground: "#7ce9b4",
  warningBackground: "#fec84b",
  errorBackground: "#f8869a",
}

export const zIndex = {
  modal: 200,
  splashPage: 199,
  animation: 150,
  plugin: 99,
  interactiveButton: 97,
  exitButton: 8,
  chat: 6,
  videoLoader: 5,
  playerTitle: 3,
  videoControls: 3,
  player: 1,
}

// Text sizes are in rem, to allow for larger base font on mobile devices
// without endless media queries. Conversion chart below for reference
// Default base font size is 14px. Mobile device base font size is 16px.

// Desktop    Rem value     Mobile
//---------------------------------
// 12 px      0.86rem       ~14px
// 13 px      0.92rem       ~15px
// 14 px      1rem          16px
// 16 px      1.14rem       ~18px
// 18 px      1.29rem       ~20px
// 20 px      1.43rem       ~22px
// 22 px      1.57rem       ~24px
// 24 px      1.71rem       ~27px

export const baseTheme = (theme: Record<string, string>) => ({
  colors: { ...theme },
  font: {
    primary: `'Roboto', sans-serif`,
    display: `'Montserrat', sans-serif`,
  },
  zIndex: { ...zIndex },
  input: {
    background: theme.white,
    borderColor: theme.neutralDark,
    borderFocus: theme.darkMed,
    borderDisabled: theme.med,
    borderRadius: "9999px",
    borderWidth: "1px",
    height: "40px",
    textColor: theme.dark,
    textSize: "1rem",
    placeholder: theme.med,
    labelSize: "0.85rem",
    labelColor: theme.med,
    errorText: "0.92rem",
    error: theme.error,
  },
  nonAccentButton: {
    bgColor: theme.black70,
  },
  button: {
    loaderSize: "18px",
    textSize: "1rem",
    textColor: theme.white,
    borderRadius: "2rem",
    iconColor: theme.med,
    bgColor: theme.accent,
    disabled: theme.med,
  },
  text: {
    dark: theme.dark,
    default: theme.dark,
    light: theme.med,
    discount: theme.blueGrey,
    link: theme.lightBlue,
    para: "1rem",
    error: theme.error,
  },
  mobile: {
    buttonMargin: "10px",
    input: { height: "44px" },
    minimumPortraitChatHeight: "60%",
  },
  background: theme.white,
  backgroundShade: theme.neutral,
  formBackground: theme.white,
  videoLetterboxBackground: theme.dark,
  main: {
    buttonMargin: "10px",
    playerPadding: "12px",
    minPanelWidth: "350px",
    maxPanelWidth: "500px",
    background: theme.white,
  },
  commerce: {
    headerHeight: "55px",
    footerHeight: "60px",
  },
  voting: {
    font: `'Roboto', sans-serif`,
    fontWeight: "400",
    fontStyle: "normal",
    buttonSelected: theme.accent,
    backgroundRGB: "30, 40, 50",
    questionFontSize: "1.14rem",
  },
})

// This takes in a color value as a hex code
// (as it is a URL param, it will not have the # at the beginning.
// And returns a dynamic color theme with that hex code as the accent color.
export const dynamicTheme = (color?: string) => {
  if (!color) {
    return colors
  }

  // css named colors must be lowercase
  if (isNamedCssColor(color.toLowerCase())) {
    return { ...colors, accent: color.toLowerCase() }
  }

  // Tests for valid color Hex codes
  const hexPattern = new RegExp("^([a-fA-F0-9]){3}$|[a-fA-F0-9]{6}$")

  // hex codes must be uppercase
  if (hexPattern.test(color.toUpperCase())) {
    return { ...colors, accent: `#${color.toUpperCase()}` }
  }

  return colors
}
